<template>
    <Dialog v-model="showModal" :disabled="isLoading">
        <DialogHeader>
            <DialogTitle>
                {{ groupIds.length > 1 ? $t('views.monitoring.groupOverview.modals.deleteGroup.title.multiple') : $t('views.monitoring.groupOverview.modals.deleteGroup.title.single') }}
            </DialogTitle>
            <DialogDescription class="pt-4">
                {{ groupIds.length > 1 ? $t('views.monitoring.groupOverview.modals.deleteGroup.description.multiple') : $t('views.monitoring.groupOverview.modals.deleteGroup.description.single') }}
            </DialogDescription>
        </DialogHeader>
        <DialogFooter>
            <DialogClose as-child>
                <CustomButton :disabled="isLoading" color-preset="white">{{ $t('views.monitoring.groupOverview.modals.deleteGroup.buttons.cancel') }}</CustomButton>
            </DialogClose>
            <CustomButton :loading="isLoading" color-preset="warning" @click="handleDelete">{{ $t('views.monitoring.groupOverview.modals.deleteGroup.buttons.delete') }}</CustomButton>
        </DialogFooter>
    </Dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { queryKeys } from '@/plugins/store/actions/queries/production-groups';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { i18n } from '@/plugins/internationalization/i18n';

const props = defineProps<{
    groupIds: number[];
}>();
const emit = defineEmits<{
    deleted: [];
}>();

const queryClient = useQueryClient();
const toast = useToast();

const showModal = defineModel<boolean>('modelValue');

const isLoading = ref(false);

async function handleDelete() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        const requests = props.groupIds.map((groupId) => ApiClient.send('delete', `monitor/production-groups/${groupId}`, { skipHandleError: true }));

        await Promise.all(requests);

        await queryClient.invalidateQueries({ queryKey: queryKeys.getProductionGroupsRoot });

        emit('deleted');

        showModal.value = false;

        const successMessage =
            props.groupIds.length > 1
                ? i18n.global.t('views.monitoring.groupOverview.modals.deleteGroup.successMessages.groupsWereDeleted')
                : i18n.global.t('views.monitoring.groupOverview.modals.deleteGroup.successMessages.groupWasDeleted');

        toast.success(successMessage);
    } catch (err) {
        const errorMessage =
            props.groupIds.length > 1
                ? i18n.global.t('views.monitoring.groupOverview.modals.deleteGroup.errors.couldNotDeleteGroups')
                : i18n.global.t('views.monitoring.groupOverview.modals.deleteGroup.errors.couldNotDeleteGroup');

        toast.error(errorMessage);
    } finally {
        isLoading.value = false;
    }
}
</script>
